import { currentDaysOnMarket } from "./daysOnMarket";
import type { CandidateComp } from "./types";

/**
 * THE COMPETING-HOMES SEARCH, ITS OWN CRITERIA (C3, 2026-11-02).
 *
 * The homes on the market are the subject's competition, never evidence of value, and the agent asks
 * different questions of them than of the sold sales: how far, how long on the market, how big, at
 * what asking price, active or pending. These criteria are INDEPENDENT of the sold search — changing
 * one never moves the other.
 *
 *   · radius          the MLS listings search's own radius (`comp_criteria.active_radius_mi`); a
 *                     change applies on Refresh, which asks the server again (MLS read-only Search).
 *   · days listed, size, price band, status
 *                     filter the listings already held, instantly, in the browser.
 *
 * STARTING VALUES ARE THE OWNER'S RULING (2026-11-02): 1 mile, any days listed, ±400 sq ft around the
 * subject's living area, any price, Active + Pending. All adjustable. The panel never starts from,
 * or follows, the sold search's values.
 *
 * Strict tier: a listing whose record does not state a field is KEPT and counted as unchecked for
 * that field, never treated as a pass or a zero. Nothing here ranks, selects or prices a listing.
 */
export type ActiveStatus = "active" | "pending";

export interface ActivesCriteria {
  radius_mi: number;
  /** Most days on the market (brought forward to today, as the column shows); null = any. */
  max_days: number | null;
  /** ± square feet of the subject's living area; null = any size. */
  gla_band_sqft: number | null;
  price_min: number | null;
  price_max: number | null;
  statuses: ActiveStatus[];
}

export const DAYS_OPTIONS = [30, 60, 90, 180];

export function startingActives(): ActivesCriteria {
  return { radius_mi: 1, max_days: null, gla_band_sqft: 400, price_min: null, price_max: null,
           statuses: ["active", "pending"] };
}

/** Fewest homes the agent should see before the panel offers to widen. */
export const TARGET_MIN_HOMES = 5;
export const RADIUS_STEPS = [0.5, 1, 2, 3, 5];
export const SIZE_STEPS = [400, 600, 800, 1000];

export interface WideningStep {
  id: string;
  /** What changes, from and to, in the agent's words. */
  what: string;
  from: string;
  to: string;
  patch: Partial<ActivesCriteria>;
  /** A radius or status change needs a new listings search; the rest only loosen the filters. */
  needsRefresh: boolean;
}

const miles = (n: number) => `${Number(n.toFixed(2))} mi`;
const sqft = (n: number) => `±${n.toLocaleString("en-US")} sq ft`;
const usd = (n: number) => `$${n.toLocaleString("en-US")}`;

/**
 * THE STATED WAYS TO WIDEN, one change each, loosest-last. The panel OFFERS these when fewer than
 * TARGET_MIN_HOMES show; the agent applies each one, and each says what it changes, from what to
 * what. Nothing here is ever applied for the agent.
 */
export function wideningSteps(k: ActivesCriteria, subjectGla: number | null): WideningStep[] {
  const out: WideningStep[] = [];
  const nextR = RADIUS_STEPS.find((r) => r > k.radius_mi + 1e-9);
  if (nextR !== undefined) {
    out.push({ id: "radius", what: "Search radius", from: miles(k.radius_mi), to: miles(nextR),
               patch: { radius_mi: nextR }, needsRefresh: true });
  }
  if (k.gla_band_sqft !== null && subjectGla) {
    const nextS = SIZE_STEPS.find((n) => n > k.gla_band_sqft! + 1e-9);
    out.push({ id: "size", what: "Size", from: sqft(k.gla_band_sqft), to: nextS !== undefined ? sqft(nextS) : "any size",
               patch: { gla_band_sqft: nextS ?? null }, needsRefresh: false });
  }
  if (k.max_days !== null) {
    out.push({ id: "days", what: "Days listed", from: `${k.max_days} days or fewer`, to: "any",
               patch: { max_days: null }, needsRefresh: false });
  }
  if (k.price_min !== null || k.price_max !== null) {
    const from = k.price_min !== null && k.price_max !== null ? `${usd(k.price_min)} to ${usd(k.price_max)}`
      : k.price_min !== null ? `${usd(k.price_min)} and up` : `up to ${usd(k.price_max!)}`;
    out.push({ id: "price", what: "Asking price", from, to: "any price",
               patch: { price_min: null, price_max: null }, needsRefresh: false });
  }
  if (k.statuses.length < 2) {
    out.push({ id: "status", what: "Status",
               from: k.statuses.length ? (k.statuses[0] === "active" ? "Active only" : "Pending only") : "none chosen",
               to: "Active + Pending", patch: { statuses: ["active", "pending"] }, needsRefresh: true });
  }
  return out;
}

const num = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

export function listingStatus(c: CandidateComp): ActiveStatus | null {
  const s = String(c.status ?? "").trim().toLowerCase();
  if (s.startsWith("pend") || s === "under contract" || s === "active under contract") return "pending";
  if (s.startsWith("act") || s === "for sale" || s === "coming soon") return "active";
  return null;
}

export interface ActivesVerdict {
  matches: boolean;
  /** Fields this listing's record does not state, in the agent's words. */
  unchecked: string[];
}

export function judgeListing(c: CandidateComp, k: ActivesCriteria, subjectGla: number | null): ActivesVerdict {
  const unchecked: string[] = [];
  let ok = true;
  const d = num(c.distance);
  if (d === null) unchecked.push("distance");
  else if (d > k.radius_mi + 1e-9) ok = false;
  if (k.max_days !== null) {
    const days = currentDaysOnMarket(c, c.dom_as_of);
    if (days === null || days === undefined) unchecked.push("days listed");
    else if (days > k.max_days) ok = false;
  }
  if (k.gla_band_sqft !== null && subjectGla) {
    const g = num(c.gla);
    if (g === null) unchecked.push("living area");
    else if (Math.abs(g - subjectGla) > k.gla_band_sqft + 1e-9) ok = false;
  }
  if (k.price_min !== null || k.price_max !== null) {
    const p = num(c.list_price);
    if (p === null) unchecked.push("asking price");
    else if ((k.price_min !== null && p < k.price_min) || (k.price_max !== null && p > k.price_max)) ok = false;
  }
  const st = listingStatus(c);
  if (st === null) unchecked.push("status");
  else if (!k.statuses.includes(st)) ok = false;
  return { matches: ok, unchecked };
}

/**
 * The indices to HIDE: listings outside the criteria that the agent has not ticked for the report.
 * A ticked listing outside the criteria stays on screen (and is counted in `keptOutside`), so
 * nothing the agent chose ever vanishes from view.
 */
export function hiddenListings(listings: readonly CandidateComp[], k: ActivesCriteria, subjectGla: number | null):
  { hidden: Set<number>; keptOutside: number; unchecked: number } {
  const hidden = new Set<number>();
  let keptOutside = 0;
  let unchecked = 0;
  listings.forEach((c, i) => {
    const v = judgeListing(c, k, subjectGla);
    if (v.unchecked.length) unchecked += 1;
    if (v.matches) return;
    if (c._selected === true) keptOutside += 1;
    else hidden.add(i);
  });
  return { hidden, keptOutside, unchecked };
}
